import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

import Icon from '../common/Icon';

const VIDEO_SRC = `${process.env.PUBLIC_URL || ''}/demo/krishvaq-demo.mp4`;
const POSTER_SRC = `${process.env.PUBLIC_URL || ''}/demo/krishvaq-demo-poster.jpg`;

// What the video actually shows, in order - kept to the real flow.
const DEMO_STEPS = [
  { icon: 'plus', title: 'Start a meeting', text: 'Pick your language, translation and AI mode.' },
  { icon: 'file-text', title: 'Add your documents', text: 'Give KrishvaQ your CV or project notes as context.' },
  { icon: 'sparkles', title: 'Get answers live', text: 'Questions are transcribed, translated and answered as they are asked.' },
];

/** True once the element has scrolled into view (immediately where
 * IntersectionObserver is unavailable, so content is never left hidden). */
function useRevealOnScroll() {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(
    () => typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    if (revealed || !ref.current) return undefined;
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [revealed]);

  return [ref, revealed];
}

/**
 * Landing-page product demo: a poster with a play button inside an app-window
 * frame. The video loads only its metadata until the visitor presses play,
 * then plays with native controls. The video is captioned and has no audio track.
 */
function DemoVideoSection() {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);

  const play = () => {
    const video = videoRef.current;
    if (!video) return;
    if (ended) video.currentTime = 0;
    setStarted(true);
    setEnded(false);
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        // Playback refused: leave the native controls visible.
      });
    }
  };

  const showOverlay = !started || ended;
  const [sectionRef, revealed] = useRevealOnScroll();
  const shown = revealed ? 'shown' : 'hidden';

  return (
    <section className="landing-section demo-video-section" id="demo">
      <div className="demo-video-inner" ref={sectionRef}>
        <motion.div
          className="demo-video-header"
          initial="hidden"
          animate={shown}
          variants={{ hidden: { opacity: 0, y: 24 }, shown: { opacity: 1, y: 0 } }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="landing-eyebrow">
            <span className="eyebrow-dot" />
            PRODUCT DEMO
          </div>
          <h2 className="demo-video-title">
            See KrishvaQ <span className="hero-title-gradient">in action.</span>
          </h2>
          <p className="demo-video-subtitle">
            Watch a live interview question get transcribed, translated and answered, right
            on top of your meeting.
          </p>
        </motion.div>

        <motion.div
          className="demo-video-frame"
          initial="hidden"
          animate={shown}
          variants={{
            hidden: { opacity: 0, y: 40, scale: 0.97 },
            shown: { opacity: 1, y: 0, scale: 1 },
          }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <div className="demo-video-glow" aria-hidden="true" />
          <div className="demo-video-window">
            <div className="demo-video-bar" aria-hidden="true">
              <span className="demo-dot red" />
              <span className="demo-dot amber" />
              <span className="demo-dot green" />
              <span className="demo-video-bar-title">KrishvaQ · Live meeting</span>
              <span className="demo-video-bar-pill">0:34</span>
            </div>

            <div className="demo-video-stage">
              <video
                ref={videoRef}
                className="demo-video"
                src={VIDEO_SRC}
                poster={POSTER_SRC}
                preload="metadata"
                playsInline
                controls={started && !ended}
                onEnded={() => setEnded(true)}
                aria-label="KrishvaQ product demo video"
              />

              {showOverlay && (
                <button
                  type="button"
                  className="demo-video-play"
                  onClick={play}
                  aria-label={ended ? 'Replay the KrishvaQ demo video' : 'Play the KrishvaQ demo video'}
                >
                  <span className="demo-play-ring" aria-hidden="true">
                    <span className="demo-play-icon">{ended ? '↻' : '▶'}</span>
                  </span>
                  <span className="demo-play-label">
                    {ended ? 'Watch again' : 'Watch KrishvaQ Demo'}
                  </span>
                </button>
              )}
            </div>
          </div>
        </motion.div>

        <div className="demo-video-steps">
          {DEMO_STEPS.map((step, index) => (
            <motion.div
              className="demo-video-step"
              key={step.title}
              initial="hidden"
              animate={shown}
              variants={{ hidden: { opacity: 0, y: 20 }, shown: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.5, delay: 0.35 + 0.1 * index, ease: 'easeOut' }}
            >
              <span className="demo-step-icon">
                <Icon name={step.icon} size={18} />
              </span>
              <div>
                <strong>
                  <span className="demo-step-number">{String(index + 1).padStart(2, '0')}</span>
                  {step.title}
                </strong>
                <p>{step.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default DemoVideoSection;
